import React, { Fragment, useEffect, useRef, useState } from "react";
import { find, equals, propEq, filter, uniq } from "ramda";
import * as R from 'ramda';
import HtmlReactParser from "html-react-parser";
import Answers from "../../components/Answers";
import { parseListenDragDropQuestion, parseDragDropQuestion } from '../pageContents/Const';
import DropQuestion from "./DropQuestion";
import { reportClientError } from "../../utils/clientLog";

/**
 * 底下題號列要顯示的每一格：`{ seq: 顯示的題號, anchor: 所屬題組的起始題號 }`。
 *
 * 多選題（例如 21-22 選兩個）在資料上是「一個題組、一筆 content」，id 只有
 * 開頭的 21——原本題號列因此只畫得出 21 和 23，22 跟 24 整個不見，
 * 學生會以為漏題。parseOptionQuestion 已經把範圍存進 content 的 ids。
 *
 * 兩顆按鈕都錨定到題組起始題號：狀態（目前題／已作答／標記複習）一起變，
 * 點哪一顆都跳到同一個題組。chosenQuestion 一律維持是題組的 id，
 * 否則作答區會找不到對應的題目而失去高亮。
 */
const getNavSeqs = (targetPage) => {
  const items = [];
  const seen = {};
  const push = (seq, anchor) => {
    if (seen[seq]) {
      return;
    }
    seen[seq] = true;
    items.push({ seq, anchor });
  };

  if (targetPage.parsedContent) {
    targetPage.parsedContent.forEach((q) => push(q.id, q.id));
  }
  targetPage.formattedGroups.forEach((formattedGroup) => {
    formattedGroup.content.forEach((q) => {
      if (!q.ids || q.ids.length < 2) {
        push(q.id, q.id);
        return;
      }
      for (let n = q.ids[0]; n <= q.ids[q.ids.length - 1]; n++) {
        push(n, q.id);
      }
    });
  });

  items.sort((a, b) => a.seq - b.seq);
  return items;
};

/** 題組層級的題號——「上一題／下一題」用這個，一次跳一個題組 */
const getQuestionSeqs = (targetPage) => {
  let seqs = [];
  if (targetPage.parsedContent) {
    seqs.push(...targetPage.parsedContent.map((q) => q.id));
  }
  targetPage.formattedGroups.forEach((formattedGroup) => {
    seqs.push(...formattedGroup.content.map((q) => q.id));
  });

  seqs = uniq(seqs);
  seqs.sort((a, b) => a - b);
  return seqs;
};

const TestPage = ({
  display,
  pageSeq,
  pages,
  page,
  testType,
  chosenQuestion,
  setChosenQuestion,
  answeredQuestion,
  setAnsweredQuestion,
  renderNextPage,
  renderPreviousPage,
  TestsPageJump,
  reviewChecked,
  setReviewChecked,
  reviewModeNode,
  setReviewModeNode,
  chooseQForCheckbox,
  setChooseQForCheckbox,
  storedAnswers,
  setStoredAnswers,
  writingAnswerNote,
  setWritingAnswerNote,
  splitRatio,
  setSplitRatio,
  fontScale,
}) => {
  const contentRef = useRef(null);

  /**
   * 文章與題目之間的拖曳分隔線。
   *
   * 兩欄原本寫死各 50%，考生沒辦法把文章拉寬一點。這裡改成用比例，
   * 拖曳時直接算游標在 .content 裡的相對位置，限制在 25%～75% 之間——
   * 再往兩邊就會有一欄窄到沒辦法讀。
   *
   * 比例存在上層（App），所以換頁、甚至換科目都會保持考生調好的寬度。
   */
  const startDrag = (event) => {
    event.preventDefault();
    const box = contentRef.current;
    if (!box) {
      return;
    }
    const rect = box.getBoundingClientRect();

    const move = (e) => {
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const ratio = ((x - rect.left) / rect.width) * 100;
      setSplitRatio(Math.min(75, Math.max(25, ratio)));
    };
    const stop = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", stop);
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", stop);
      document.body.classList.remove("is-dragging-divider");
    };

    document.body.classList.add("is-dragging-divider");
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", stop);
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("touchend", stop);
  };
  const [checkboxPages, setCheckboxPages] = useState([]);
  const [matchingHeadingPages, setMatchingHeadingPages] = useState([]);
  const [replacedText, setReplacedText] = useState("");

  useEffect(() => {
    const hasCheckbox = propEq("type", "checkboxes");
    const hasCheckboxPages = filter((filterPage) => {
      for (let group of filterPage.formattedGroups) {
        if (!hasCheckbox(group)) {
          continue;
        }
        return true;
      }
      return false;
    })(pages);
    setCheckboxPages(
      Object.values(hasCheckboxPages).map((_, pageIndex) => pageIndex + 1)
    );

    const hasMatchingHeading = propEq("questionType", "drop in answer part");
    const hasMatchingHeadingPages = filter(hasMatchingHeading, pages);
    setMatchingHeadingPages(
      Object.values(hasMatchingHeadingPages).map(
        (_, pageIndex) => pageIndex + 1
      )
    );
  }, [pages]);

  // 點開題目
  const toggleQuestion = (chosenId, targetPageSeq) => {
    if (targetPageSeq !== pageSeq) {
      TestsPageJump(targetPageSeq + 3);
    }

    setChosenQuestion(chosenId);
  };

  // 檢查題目
  const checkAnsweredQuestion = (questionId, answersStatus) => {
    const newAnsweredQuestion = [...answeredQuestion];
    const questionAnswered = answersStatus.filter(
      (checked) => checked === true
    );

    if (questionAnswered.length === 0) {
      newAnsweredQuestion[questionId - 1] = false;
    } else {
      newAnsweredQuestion[questionId - 1] = true;
    }
    setAnsweredQuestion(newAnsweredQuestion);
  };

  // 填答案
  //
  // 記錄作答的這一步若出錯，原本整個函式中斷、答案沒記下來，考生卻看得到自己
  // 點的選項、打的字——完全不會發現。2026-10-18 兩位考生整科 0 筆作答，原因未明。
  // 現在出錯會回報伺服器，並且至少把這一題記下來（略過「拖拉題不可重複」的整理）。
  const storeAnswers = (questionId, answer) => {
    try {
      storeAnswersChecked(questionId, answer);
    } catch (e) {
      reportClientError("storeAnswers", e, `q=${questionId}`);
      setStoredAnswers([
        ...storedAnswers.filter((ans) => ans.questionId !== questionId),
        { questionId, answer },
      ]);
    }
  };

  const storeAnswersChecked = (questionId, answer) => {
    let newStoredAnswers = storedAnswers.filter(
      (ans) => ans.questionId !== questionId
    );

    /* 拖拉類型的題目不能重複相同答案 */
    let sameDragSeqs = [];
    for (let p of pages) {
      if (!p.groups) {
        break;
      }
      for (let g of p.groups) {
        // console.log(g);
        if (!R.is(Array, g.questions) || g.type.split('-')[0] !== 'DRAG') {
          continue;
        }
        sameDragSeqs = (g.type === 'DRAG-Common' ? parseListenDragDropQuestion : parseDragDropQuestion)(g).content.map(c => c.id)
        if (!sameDragSeqs.includes(questionId)) {
          sameDragSeqs = [];
          continue;
        }
        break;
      }
      if (sameDragSeqs.length > 0) {
        break;
      }
    }

    if (sameDragSeqs.length > 0 && answer !== '') {
      // console.log(sameDragSeqs, answer);
      newStoredAnswers = newStoredAnswers.filter(
        (ans) => !sameDragSeqs.includes(ans.questionId) || ans.answer !== answer
      );
    }

    newStoredAnswers = [...newStoredAnswers, { questionId, answer }];

    // console.log(pages);
    // console.log(newStoredAnswers);
    setStoredAnswers(newStoredAnswers);
  };

  // 寫作填答
  const updateAnwerPerPage = (content, pageNum) => {
    const newWritingAnswerNote = [...writingAnswerNote];
    newWritingAnswerNote[pageNum - 1] = content;
    setWritingAnswerNote(newWritingAnswerNote);
  };

  const renderReviewNode = () => {
    const newReviewModeNode = [...reviewModeNode];
    const newReviewChecked = !reviewChecked;
    setReviewChecked(newReviewChecked);

    if (find(equals(pageSeq))(checkboxPages)) {
      if (newReviewChecked) {
        newReviewModeNode[chooseQForCheckbox - 1] = true;
      } else {
        newReviewModeNode[chooseQForCheckbox - 1] = false;
      }

      setReviewModeNode(newReviewModeNode);
    } else {
      if (newReviewChecked) {
        newReviewModeNode[chosenQuestion - 1] = true;
      } else {
        newReviewModeNode[chosenQuestion - 1] = false;
      }

      setReviewModeNode(newReviewModeNode);
    }
  };

  const renderNavAnswers = (navPage) => {
    // seq 只用來顯示；狀態與點擊一律用 anchor（題組的起始題號）
    const getItem = ({ seq, anchor }) => (
      <li key={seq}>
        <button
          className={`btn btn-primary font-weight-bolder ${
            chosenQuestion === anchor ? "highlight" : ""
          } ${reviewModeNode[anchor - 1] ? "for-review" : ""} ${
            answeredQuestion[anchor - 1] ? "answered" : ""
          }`}
          onClick={() => {
            toggleQuestion(anchor, navPage.page);
            setReviewChecked(!!reviewModeNode[anchor - 1]);
          }}
        >
          {seq}
        </button>
      </li>
    );

    return getNavSeqs(navPage).map((navItem) => {
      setTimeout(() => {
        document.querySelectorAll(".question-buttons").forEach((wrapper) => {
          const wrapperRect = wrapper.getBoundingClientRect();
          if (wrapperRect.x === 0 || wrapperRect.y === 0) {
            return;
          }

          const xRange = [wrapperRect.x, wrapperRect.x + wrapperRect.width];

          const highlight = wrapper.querySelector(".highlight");
          if (!highlight) {
            return;
          }

          const elRect = highlight.getBoundingClientRect();
          if (xRange[0] < elRect.x && xRange[1] > elRect.x) {
            return;
          }

          highlight.scrollIntoView({
            block: "nearest",
            inline: "center",
          });
        });
      }, 100);
      return getItem(navItem);
    });
  };

  const renderAllNavAnswers = () => {
    return pages.map((navPage, i) => {
      return (
        <Fragment key={i}>
          <li className="part-label-holder">
            <div className="btn btn-primary disabled part-label">
              {`Page ${i + 1}`}:
            </div>
          </li>
          {renderNavAnswers(navPage)}
        </Fragment>
      );
    });
  };

  const handleNextClick = () => {
    const seqs = getQuestionSeqs(page);
    const newChosenQuestion = chosenQuestion + 1;
    setChosenQuestion(newChosenQuestion);
    if (newChosenQuestion > seqs[seqs.length - 1]) {
      renderNextPage();
    }
  };

  const handlePreviousClick = () => {
    const seqs = getQuestionSeqs(page);
    const newChosenQuestion = chosenQuestion - 1;
    setChosenQuestion(newChosenQuestion);
    if (newChosenQuestion < seqs[0]) {
      renderPreviousPage();
    }
  };

  return (
    <div
      className="container-fluid px-lg-5 h-100"
      style={display ? {} : { display: "none" }}
    >
      <div className="row mx-lg-n5 h-100">
        <div className="test-container container-fluid px-lg-5">
          <div className="title">
            <h4>{page.title ?? `Page ${pageSeq}`}</h4>
            <p>{page.description}</p>
          </div>
          <div
            ref={contentRef}
            className={`content ${page.formattedGroups.map(
              (group) => group.className
            )} ${testType === "L" ? "listening-test" : ""} `}
            style={{ "--exam-font-scale": fontScale }}
          >
            {testType !== "L" && (
              <div className="question" style={{ width: `${splitRatio}%` }}>
                {page.questionType !== "drop in answer part" ? (
                  HtmlReactParser(page.content)
                ) : (
                  <DropQuestion
                    questions={page.parsedContent}
                    preContent={page.preContent}
                    postContent={page.postContent}
                    className="drop-target"
                    chosenQuestion={chosenQuestion}
                    toggleQuestion={toggleQuestion}
                    checkAnsweredQuestion={checkAnsweredQuestion}
                    generateReplacedText={setReplacedText}
                    storeAnswers={storeAnswers}
                    storedAnswers={storedAnswers}
                  />
                )}
              </div>
            )}
            {/* 拖曳分隔線：聽力只有一欄，不需要 */}
            {testType !== "L" && (
              <div
                className="pane-divider"
                role="separator"
                aria-orientation="vertical"
                title="拖曳可調整文章與題目的寬度"
                onMouseDown={startDrag}
                onTouchStart={startDrag}
              >
                <span className="pane-divider__grip" />
              </div>
            )}
            <div
              className="answer"
              style={testType !== "L" ? { width: `${100 - splitRatio}%` } : undefined}
            >
              {page.formattedGroups.map((group, groupIndex) => (
                <Answers
                  key={groupIndex}
                  answers={group}
                  chosenQuestion={chosenQuestion}
                  toggleQuestion={toggleQuestion}
                  checkAnsweredQuestion={checkAnsweredQuestion}
                  generateReplacedText={setReplacedText}
                  replacedText={replacedText}
                  storeAnswers={storeAnswers}
                  storedAnswers={storedAnswers}
                  writingAnswerNote={
                    writingAnswerNote[pageSeq - 1]
                      ? writingAnswerNote[pageSeq - 1]
                      : ""
                  }
                  updateAnwerPerPage={updateAnwerPerPage}
                  pageNumber={pageSeq}
                />
              ))}
            </div>
          </div>
          <div className="row question-navigation d-none d-lg-flex">
            <div className="col col-lg-1 col-xs-12 col-sm-6 order-2 order-lg-1 input-group">
              <button
                className="btn btn-block review"
                onClick={renderReviewNode}
              >
                <span
                  className={`check ${reviewChecked ? "checked" : ""}`}
                ></span>
                Review
              </button>
            </div>
            <div className="col col-lg-9 col-xs-12 order-1 order-xs-1 order-sm-1 question-nav">
              <ul className="question-buttons">{renderAllNavAnswers()}</ul>
            </div>
            <div
              className="col col-lg-2 col-xs-12 col-sm-6 order-3 order-xs-3 order-sm-3 arrows"
              data-total-questions="40"
            >
              <div className="row">
                <div className="col">
                  <button
                    className="btn btn-primary prev"
                    onClick={handlePreviousClick}
                    disabled={chosenQuestion - 1 < 1}
                  >
                    <span className="arrow-left"></span>
                  </button>
                  <button
                    className="btn btn-primary next"
                    onClick={handleNextClick}
                  >
                    <span className="arrow-right"></span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestPage;
